import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import {
  Close,
  CheckCircle,
  CameraAlt,
  Upload,
  AutoAwesome
} from '@mui/icons-material';
import { processProductImage } from '../utils/photoScanner';
import { isSubscriptionActive } from '../utils/stripeHelper';

function PhotoScanner({ open, onClose, onScanSuccess, familyData }) {
  const isPremium = isSubscriptionActive(familyData);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const checkPremiumAccess = () => {
    if (!isPremium) {
      setShowUpgradeDialog(true);
      return false;
    }
    return true;
  };

  const handleFileSelect = async (event) => {
    if (!checkPremiumAccess()) return;
    const file = event.target.files[0];
    if (!file) return;

    // Clear the input value to allow selecting the same file again
    event.target.value = '';

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image too large. Please choose an image under 5MB');
      return;
    }

    setError('');
    setProcessing(true);

    try {
      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);

      // Process image
      const result = await processProductImage(file);
      setProcessedData(result);

      // For now, let user manually enter details
      // In production, this would extract text automatically
      handleManualEntry(result);
    } catch (err) {
      console.error('Photo processing error:', err);
      setError('Failed to process image. Please try again or enter details manually.');
    } finally {
      setProcessing(false);
    }
  };

  const handleManualEntry = (result) => {
    // Pass data back to parent for manual completion
    onScanSuccess({
      method: 'photo',
      imageUrl: result.imageUrl,
      needsManualEntry: true,
      suggestion: result.suggestion
    });
    handleClose();
  };

  const handleClose = () => {
    // Cleanup
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    setProcessedData(null);
    setProcessing(false);
    setError('');
    onClose();
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          📸 Scan Product Photo
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center' }}>
          {!preview && !processing && (
            <>
              <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
                Upload a photo or screenshot of a product
              </Typography>

              {/* Upload Options */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<CameraAlt />}
                  onClick={handleCameraClick}
                  size="large"
                >
                  Take Photo
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  onClick={handleUploadClick}
                  size="large"
                >
                  Upload Image
                </Button>
              </Box>

              {/* Hidden file inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />

              {/* What you can scan */}
              <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
                <Typography variant="subtitle2" gutterBottom>
                  📱 What You Can Scan:
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'left' }}>
                  • Amazon product pages (screenshots)<br />
                  • Walmart app screenshots<br />
                  • Product packaging<br />
                  • Price tags<br />
                  • Grocery flyers
                </Typography>
              </Paper>
            </>
          )}

          {/* Preview */}
          {preview && !processing && (
            <Box>
              <img
                src={preview}
                alt="Product preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}
              />
              <Alert severity="info" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoAwesome />
                  Image uploaded! I'll help you add the details.
                </Box>
              </Alert>
            </Box>
          )}

          {/* Processing */}
          {processing && (
            <Box sx={{ py: 4 }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                🤖 Analyzing Image...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This will only take a moment
              </Typography>
            </Box>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Success */}
          {processedData && !processing && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle />
                Image processed! Fill in the details in the next screen.
              </Box>
            </Alert>
          )}

          {/* Instructions */}
          {!preview && !processing && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                💡 <strong>Tip:</strong> Make sure the product name and price are clearly visible in the photo for best results.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} startIcon={<Close />}>
          {preview ? 'Cancel' : 'Close'}
        </Button>
        {preview && !processing && !processedData && (
          <Button
            variant="contained"
            onClick={handleUploadClick}
          >
            Choose Different Image
          </Button>
        )}
      </DialogActions>

      {/* Upgrade Dialog for Non-Premium Users */}
      <Dialog open={showUpgradeDialog} onClose={() => setShowUpgradeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome sx={{ color: 'warning.main' }} />
            Unlock Photo Scanner
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Photo scanning is a <strong>Premium feature</strong>! Upgrade to Earn-E Premium to:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2">📸 Snap photos of receipts &amp; products</Typography>
            <Typography variant="body2">🤖 AI extracts items automatically</Typography>
            <Typography variant="body2">⚡ Add multiple items in seconds</Typography>
            <Typography variant="body2">💰 Never type shopping items manually</Typography>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              🎉 Start your <strong>14-day free trial</strong> - cancel anytime!
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowUpgradeDialog(false)} variant="outlined">
            Maybe Later
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowUpgradeDialog(false);
              window.location.href = '/subscription';
            }}
            startIcon={<AutoAwesome />}
          >
            Start Free Trial
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default PhotoScanner;


